import React, { useState, useEffect } from "react";
import { Loader, Icon, Table, Dropdown } from "semantic-ui-react";
import { map, size } from "lodash";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { HeaderPage, PrintHeader, PrintTable } from "../../components/Admin";
import { usePayment, useOrder, usePrintOrientation, usePrinting } from "../../hooks";
import { getBusinessToday } from "../../utils/date";
import { buildDashboard } from "../../utils/dashboard";
import { formatMinutes } from "../../utils/prepTimes";
import "./DashboardReport.scss";

// Color de marca para la única serie del gráfico (una sola serie: sin
// leyenda, el título la nombra). El texto nunca va en este color.
const BRAND = "#e67e22";


// Modos del gráfico principal (igual que el Dashboard de Restaurant.pe).
const CHART_MODES = {
  revenue: { text: "Dinero por ventas", title: "Ventas por hora (S/)" },
  count: { text: "Cantidad de ventas", title: "Cantidad de ventas por hora" },
};
const CHART_MODE_OPTIONS = map(CHART_MODES, (mode, key) => ({
  key,
  value: key,
  text: mode.text,
}));
const money = (value) => `S/ ${Number(value || 0).toFixed(2)}`;
// En tablas el "S/" va en la cabecera ("Importe (S/)"), no en cada fila:
// así la cifra no se parte en dos líneas en columnas angostas.
const amount = (value) => Number(value || 0).toFixed(2);

// "Panel del día": vista rápida de cómo va el día, inspirada en el Dashboard
// de Restaurant.pe. No confundir con "Resumen Diario" (DailySummary.js), que
// es el detalle de pagos por tipo.
export function DashboardReport() {
  const [dateFrom, setDateFrom] = useState(getBusinessToday);
  const [dateTo, setDateTo] = useState(getBusinessToday);
  const [chartMode, setChartMode] = useState("revenue");
  const {
    loading: loadingPayments,
    payments,
    getPaymentsInRange,
  } = usePayment();
  const {
    loading: loadingOrders,
    orders,
    getOrdersInRange,
    getCancellations,
  } = useOrder();
  const { getVoidedPayments } = usePayment();
  // Anulaciones del rango (pedidos cancelados + ventas anuladas): tarjeta
  // aparte, no cambia ningún otro cálculo del panel.
  const [voids, setVoids] = useState(null);
  usePrintOrientation("portrait");

  useEffect(() => {
    if (dateFrom && dateTo) {
      getPaymentsInRange(dateFrom, dateTo);
      getOrdersInRange(dateFrom, dateTo);
      Promise.all([
        getCancellations(dateFrom, dateTo),
        getVoidedPayments(dateFrom, dateTo),
      ])
        .then(([cancellations, voided]) => setVoids({ cancellations, voided }))
        .catch(() => setVoids(null));
    }
  }, [dateFrom, dateTo]);

  const loading = loadingPayments || loadingOrders;
  const data =
    !loading && Array.isArray(payments) && Array.isArray(orders)
      ? buildDashboard(payments, orders)
      : null;

  const dateRangeLabel =
    dateFrom === dateTo
      ? moment(dateFrom).format("DD/MM/YYYY")
      : `Del ${moment(dateFrom).format("DD/MM/YYYY")} al ${moment(
          dateTo,
        ).format("DD/MM/YYYY")}`;

  return (
    <>
      <div className="screen-only">
        <HeaderPage
          title="Panel del día"
          btnTitle={
            <>
              <Icon name="print" /> Imprimir
            </>
          }
          btnClick={() => window.print()}
        />
      </div>

      <div className="screen-only dashboard-report__filters">
        <div>
          <label>Desde</label>
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label>Hasta</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      <PrintTable>
        <PrintHeader title="Panel del día" subtitle={dateRangeLabel} />

        {!data ? (
          <Loader active inline="centered">
            Cargando...
          </Loader>
        ) : data.salesCount === 0 && data.lossCount === 0 ? (
          <p className="print-content">No hay ventas cobradas en este rango.</p>
        ) : (
          <div className="print-content dashboard-report">
            <div className="dashboard-report__tiles">
              <Tile label="Ventas" value={money(data.totalSales)}>
                {data.salesCount} venta(s) cobrada(s)
              </Tile>
              <Tile label="Ticket promedio" value={money(data.avgTicket)}>
                por venta
              </Tile>
              {map(data.byMethod, (method) => (
                <Tile key={method.value} label={method.text} value={money(method.total)}>
                  {method.pct.toFixed(1)}% de las ventas
                </Tile>
              ))}
              <Tile label="Mesas atendidas" value={data.tablesServed}>
                {data.itemsSold} plato(s)/producto(s) vendidos
              </Tile>
              <Tile
                label="Tiempo promedio en mesa"
                value={
                  data.avgStayMinutes != null
                    ? formatMinutes(data.avgStayMinutes)
                    : "—"
                }
              >
                del primer pedido al cobro
              </Tile>
              <Tile
                label="Tiempo de despacho"
                value={data.dispatch ? formatMinutes(data.dispatch.average) : "—"}
              >
                {data.dispatch ? (
                  <>
                    {Math.round(data.dispatch.min)}′ mín –{" "}
                    {Math.round(data.dispatch.max)}′ máx ·{" "}
                    <Link to="/admin/prep-times" className="screen-only">
                      ver detalle
                    </Link>
                  </>
                ) : (
                  "sin platos marcados Listo en Cocina"
                )}
              </Tile>
              <Tile
                label="Mozo del día"
                value={data.topWaiter ? data.topWaiter.name : "—"}
              >
                {data.topWaiter
                  ? `${data.topWaiter.salesCount} venta(s) · ${money(
                      data.topWaiter.revenue
                    )} · ${data.topWaiter.share.toFixed(1)}% de las ventas`
                  : "sin ventas registradas por el personal"}
              </Tile>
              <Tile
                label="Comensales atendidos"
                value={data.guestsSalesCount > 0 ? data.guestsTotal : "—"}
              >
                {data.guestsSalesCount > 0
                  ? `en ${data.guestsSalesCount} de ${data.salonSalesCount} cuenta(s) del salón con personas marcadas`
                  : "marca las personas al abrir una mesa del salón"}
              </Tile>
              <Tile
                label="Ticket por persona"
                value={data.ticketPerPerson != null ? money(data.ticketPerPerson) : "—"}
              >
                afluencia · sobre {data.guestsSalesCount} de {data.salonSalesCount} cuenta(s) del salón
              </Tile>
              <Tile label="Por canal de venta" value={null}>
                {map(data.byChannel, (channel) => (
                  <span key={channel.value} className="dashboard-report__channel">
                    <Icon name={channel.icon} />
                    {channel.text}: <strong>{money(channel.total)}</strong> ·{" "}
                    {channel.count} venta(s)
                  </span>
                ))}
                {/* Delivery desglosado por app (PedidosYa, Rappi…). */}
                {map(data.deliveryByChannel, (channel) => (
                  <span
                    key={channel.value}
                    className="dashboard-report__channel dashboard-report__channel--sub"
                  >
                    ↳ {channel.text}: {money(channel.total)} · {channel.count}
                  </span>
                ))}
                {data.deliveryFeeTotal > 0 && (
                  <span className="dashboard-report__channel dashboard-report__channel--sub">
                    ↳ Envíos cobrados: {money(data.deliveryFeeTotal)}
                  </span>
                )}
              </Tile>
              {/* Solo con ventas en dos o más ambientes (Salón, Terraza…) del período. */}
              {size(data.byPlace) > 1 && (
                <Tile label="Por ambiente" value={null}>
                  {map(data.byPlace, (place) => (
                    <span key={place.name} className="dashboard-report__channel">
                      {place.name}: <strong>{money(place.total)}</strong> · {place.count}{" "}
                      {place.count === 1 ? "venta" : "ventas"}
                      {/* Con una sola venta el promedio es igual al total: no se repite. */}
                      {place.count > 1 && ` · promedio ${money(place.ticket)}`}
                    </span>
                  ))}
                </Tile>
              )}
              <Tile
                label="Descuentos y cortesías"
                value={money(data.discountTotal + data.courtesyTotal)}
              >
                {data.discountCount} cuenta(s) con descuento · {data.courtesyCount} plato(s)
                de cortesía
                {data.discountTotal + data.courtesyTotal > 0 &&
                  ` · ${(
                    ((data.discountTotal + data.courtesyTotal) /
                      (data.totalSales + data.discountTotal + data.courtesyTotal)) *
                    100
                  ).toFixed(1)}% de lo consumido`}
              </Tile>
              <VoidsTile voids={voids} totalSales={data.totalSales} />
              <Tile
                label="Pérdidas"
                value={money(data.lossTotal)}
                warn={data.lossCount > 0}
              >
                {data.lossCount} mesa(s) liberada(s) sin pagar
              </Tile>
            </div>

            <div className="dashboard-report__section dashboard-report__hours">
              <div className="dashboard-report__chart-mode screen-only">
                <Dropdown
                  selection
                  options={CHART_MODE_OPTIONS}
                  value={chartMode}
                  onChange={(_, { value }) => setChartMode(value)}
                />
              </div>

              <div className="dashboard-report__hours-row">
                {/* A la derecha y pegadas al gráfico, para que se lean como
                    parte de él (como en Restaurant.pe), no como datos sueltos. */}
                <div className="dashboard-report__hours-summary">
                  <p className="dashboard-report__muted">Hora punta</p>
                  <p className="dashboard-report__big">
                    {(chartMode === "count" ? data.peakHourByCount : data.peakHour)
                      ?.label || "—"}
                  </p>
                  <p className="dashboard-report__muted">Hora más libre</p>
                  <p className="dashboard-report__big">
                    {(chartMode === "count" ? data.quietHourByCount : data.quietHour)
                      ?.label || "—"}
                  </p>
                </div>

                <div className="dashboard-report__chart">
                  {size(data.hourly) > 0 ? (
                    <HourlyChart
                      title={CHART_MODES[chartMode].title}
                      data={data.hourly}
                      dataKey={chartMode}
                    />
                  ) : (
                    <>
                      <h4>{CHART_MODES[chartMode].title}</h4>
                      <p className="dashboard-report__muted">
                        Sin ventas en el horario del local.
                      </p>
                    </>
                  )}
                </div>
              </div>

              {size(data.hourly) > 0 && (
                <div className="dashboard-report__ticket-chart">
                  <HourlyChart
                    title="Ticket promedio por hora (S/)"
                    subtitle="cuánto gasta en promedio cada cuenta cobrada en esa hora"
                    data={data.hourly}
                    dataKey="avgTicket"
                  />
                </div>
              )}
            </div>

            <div className="dashboard-report__grid">
              <RankingTable
                title="Más vendidos"
                subtitle="por cantidad"
                rows={data.topByQuantity}
              />
              <RankingTable
                title="Con mejor facturación"
                subtitle="por importe"
                rows={data.topByRevenue}
              />
            </div>

            {size(data.byArea) > 0 && (
              <div className="dashboard-report__grid">
                {map(data.byArea, (area) => (
                  <RankingTable
                    key={area.area}
                    title={`Más vendidos en ${area.area}`}
                    subtitle={`${area.quantity} und. · ${money(area.revenue)}`}
                    rows={area.products}
                  />
                ))}
              </div>
            )}

            {/* Debajo de "Más vendidos en {área}" y antes del Ranking de
                mozos (pedido del usuario): de lo que se vende a quién lo vende. */}
            <div className="dashboard-report__section">
              <RankingTable
                title="Ventas por categoría"
                rows={data.byCategory}
                nameHeader="Categoría"
                wide
              />
            </div>

            <div className="dashboard-report__section dashboard-report__ranking">
              <h4>
                Ranking de mozos
                <span className="dashboard-report__muted">
                  {" "}
                  — cada venta se atribuye a quien registró su primer pedido
                </span>
              </h4>
              {size(data.waiterRanking) === 0 ? (
                <p className="dashboard-report__muted">Sin datos.</p>
              ) : (
                <Table compact unstackable className="dashboard-report__wide-table">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>#</Table.HeaderCell>
                      <Table.HeaderCell>Mozo</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">
                        Ventas
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        textAlign="right"
                        className="dashboard-report__nowrap"
                      >
                        Importe (S/)
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">%</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {map(data.waiterRanking, (row, index) => (
                      <Table.Row key={row.name}>
                        <Table.Cell>{index + 1}</Table.Cell>
                        <Table.Cell>{row.name}</Table.Cell>
                        <Table.Cell textAlign="right">
                          {row.salesCount}
                        </Table.Cell>
                        <Table.Cell
                          textAlign="right"
                          className="dashboard-report__nowrap"
                        >
                          {amount(row.revenue)}
                        </Table.Cell>
                        <Table.Cell
                          textAlign="right"
                          className="dashboard-report__nowrap"
                        >
                          {row.share.toFixed(1)}%
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </div>

            <div className="dashboard-report__section">
              <RankingTable
                title="Más ventas por cliente"
                subtitle="cliente elegido en la cuenta"
                rows={data.byCustomer}
                nameHeader="Cliente"
                quantityHeader="Ventas"
                wide
              />
            </div>

          </div>
        )}
      </PrintTable>
    </>
  );
}

// "Anulaciones" (su "Ventas anuladas" de Restaurant.pe): pedidos cancelados
// antes de cobrar + ventas cobradas que se anularon después. Dato de control.
function VoidsTile(props) {
  const { voids, totalSales } = props;
  if (!voids) return null;
  const cancelledTotal = voids.cancellations.reduce(
    (acc, row) => acc + Number(row.price || 0),
    0
  );
  const voidedTotal = voids.voided.reduce(
    (acc, row) => acc + Number(row.totalPayment || 0),
    0
  );
  const count = voids.cancellations.length + voids.voided.length;
  const total = cancelledTotal + voidedTotal;
  const share = totalSales > 0 ? (total / totalSales) * 100 : 0;
  return (
    <Tile label="Anulaciones" value={money(total)} warn={count > 0}>
      {voids.cancellations.length} pedido(s) cancelado(s)
      {voids.voided.length > 0 && ` · ${voids.voided.length} venta(s) anulada(s)`} ·{" "}
      {share.toFixed(1)}% de las ventas ·{" "}
      <Link to="/admin/cancellations" className="screen-only">
        ver detalle
      </Link>
    </Tile>
  );
}

function Tile(props) {
  const { label, value, children, warn } = props;
  return (
    <div
      className={`dashboard-report__tile${warn ? " dashboard-report__tile--warn" : ""}`}
    >
      <p className="dashboard-report__tile-label">
        {warn && <Icon name="exclamation triangle" />}
        {label}
      </p>
      {value != null && <p className="dashboard-report__tile-value">{value}</p>}
      <p className="dashboard-report__muted">{children}</p>
    </div>
  );
}

function RankingTable(props) {
  // wide: tabla de ancho completo con columnas fijas, alineadas con las del
  // Ranking de mozos (ver .dashboard-report__wide-table).
  const {
    title,
    subtitle,
    rows,
    nameHeader = "Producto",
    quantityHeader = "Cant.",
    wide = false,
  } = props;
  return (
    <div className="dashboard-report__ranking">
      <h4>
        {title}
        {subtitle && (
          <span className="dashboard-report__muted"> — {subtitle}</span>
        )}
      </h4>
      {size(rows) === 0 ? (
        <p className="dashboard-report__muted">Sin datos.</p>
      ) : (
        <Table
          compact
          unstackable
          className={wide ? "dashboard-report__wide-table" : undefined}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>#</Table.HeaderCell>
              <Table.HeaderCell>{nameHeader}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{quantityHeader}</Table.HeaderCell>
              <Table.HeaderCell
                textAlign="right"
                className="dashboard-report__nowrap"
              >
                Importe (S/)
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="right">%</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {map(rows, (row, index) => (
              <Table.Row key={row.title}>
                <Table.Cell>{index + 1}</Table.Cell>
                <Table.Cell>{row.title}</Table.Cell>
                <Table.Cell textAlign="right">{row.quantity}</Table.Cell>
                <Table.Cell
                  textAlign="right"
                  className="dashboard-report__nowrap"
                >
                  {amount(row.revenue)}
                </Table.Cell>
                <Table.Cell
                  textAlign="right"
                  className="dashboard-report__nowrap"
                >
                  {row.share.toFixed(1)}%
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </div>
  );
}

// Ancho del gráfico al imprimir (hoja vertical, al lado de "Hora punta").
const PRINT_CHART_WIDTH = 560;

function HourlyChart(props) {
  const { title, subtitle, data, dataKey } = props;
  const printing = usePrinting();
  return (
    <div className="dashboard-report__chart-block">
      <h4>
        {title}
        {subtitle && <span className="dashboard-report__muted"> — {subtitle}</span>}
      </h4>
      <ResponsiveContainer width={printing ? PRINT_CHART_WIDTH : "100%"} height={200}>
        <AreaChart data={data} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="#eee" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12, fill: "#666" }}
            tickLine={false}
            axisLine={{ stroke: "#ddd" }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#666" }}
            tickLine={false}
            axisLine={false}
            width={48}
            allowDecimals={dataKey !== "count"}
          />
          <Tooltip content={<HourTooltip />} />
          <Area
            type="monotone"
            dataKey={dataKey}
            // Sin conectar los null: en "Ticket promedio" una hora sin ventas
            // queda como hueco, no como una recta plana que simula ventas.
            connectNulls={false}
            stroke={BRAND}
            strokeWidth={2}
            fill={BRAND}
            fillOpacity={0.18}
            dot={{ r: 3, fill: "#fff", stroke: BRAND, strokeWidth: 2 }}
            activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function HourTooltip(props) {
  const { active, payload } = props;
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="dashboard-report__tooltip">
      <strong>{point.label}</strong>
      <div>{money(point.revenue)}</div>
      <div className="dashboard-report__muted">{point.count} venta(s)</div>
      {point.avgTicket != null && (
        <div className="dashboard-report__muted">
          Ticket promedio: {money(point.avgTicket)}
        </div>
      )}
    </div>
  );
}
